import React, { useEffect, useState } from 'react';
import { MapContainer as LeafletMap, TileLayer, useMapEvents, ZoomControl, useMap as useLeafletMap } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import { useMap } from '../../contexts/MapContext';
import GazaOverlay from './GazaOverlay';
import DataLayers from './DataLayers';
import LocationMarker from './LocationMarker';
import gazaBoundary from '../../data/gaza-boundary.json';
import 'leaflet/dist/leaflet.css';

const MapEvents: React.FC = () => {
  useMapEvents({
    locationfound: () => {
      // Don't auto-zoom on location found, we'll fit to Gaza bounds instead
    },
  });
  return null;
};

// Component to fit the map to Gaza bounds once loaded
const FitToGazaBounds: React.FC = () => {
  const map = useLeafletMap();
  const { userLocation, gazaPosition } = useMap();
  
  useEffect(() => {
    if (!userLocation || !gazaBoundary?.geometry?.coordinates?.[0]) return;
    
    const targetPosition = gazaPosition || userLocation;
    const [targetLat, targetLng] = Array.isArray(targetPosition) 
      ? targetPosition 
      : [targetPosition.lat, targetPosition.lng];
    
    // Calculate Gaza's actual center
    const coords = gazaBoundary.geometry.coordinates[0];
    let sumLat = 0, sumLng = 0;
    coords.forEach(coord => {
      sumLat += coord[1];
      sumLng += coord[0];
    });
    const gazaCenterLat = sumLat / coords.length;
    const gazaCenterLng = sumLng / coords.length;
    
    // Calculate offset
    const latOffset = targetLat - gazaCenterLat;
    const lngOffset = targetLng - gazaCenterLng;
    
    // Transform Gaza coordinates to overlay position
    const transformedCoords = coords.map(coord => 
      L.latLng(coord[1] + latOffset, coord[0] + lngOffset)
    );
    
    // Create bounds from transformed coordinates
    const bounds = L.latLngBounds(transformedCoords);
    
    // Fit the map to show the entire Gaza overlay with some padding
    setTimeout(() => {
      map.fitBounds(bounds, {
        padding: [50, 50], // Add padding so the overlay isn't at the very edge
        maxZoom: 13, // Don't zoom in too much
        animate: true,
        duration: 1.5
      });
    }, 500); // Small delay to ensure overlay is rendered
  }, [map, userLocation, gazaPosition]);
  
  return null;
};

const MapContainer: React.FC = () => {
  const { userLocation, setUserLocation } = useMap();
  const [mapCenter, setMapCenter] = useState<LatLngExpression>([31.5, 34.5]); // Default to Gaza region
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get user's location
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: LatLngExpression = [
            position.coords.latitude,
            position.coords.longitude,
          ];
          setUserLocation(location);
          setMapCenter(location);
          setIsLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          // Default to a major city if geolocation fails
          const defaultLocation: LatLngExpression = [40.7128, -74.0060]; // New York
          setUserLocation(defaultLocation);
          setMapCenter(defaultLocation);
          setIsLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      setIsLoading(false);
    }
  }, [setUserLocation]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%',
        backgroundColor: '#f5f5f5',
        flexDirection: 'column',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '10px' }}>Locating your position...</div>
          <small style={{ color: '#666' }}>
            We'll overlay Gaza's boundaries on your location to help you understand its scale
          </small>
        </div>
      </div>
    );
  }

  return (
    <LeafletMap
      center={mapCenter}
      zoom={10} // Initial zoom, will be overridden by FitToGazaBounds
      style={{ height: '100%', width: '100%' }}
      zoomControl={false}
      dragging={true}
      touchZoom={true}
      scrollWheelZoom={true}
      doubleClickZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        className="grayscale-tiles"
      />
      <ZoomControl position="topright" />
      <MapEvents />
      <FitToGazaBounds />
      {userLocation && <LocationMarker />}
      <GazaOverlay />
      <DataLayers />
    </LeafletMap>
  );
};

export default MapContainer;